import {
  Text,
  HStack,
  VStack,
  StackProps,
  Button,
  Tooltip,
} from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import { BILLING_OWNER_REQUIRED_MESSAGE } from "../../components/organisms/taxReport/ActionOrPurchaseButton";
import { canUnlockPlanFeature } from "../../helper/canUnlockPlanFeature";
import { Plan } from "../../types/plans/plan";
import { OrDivider } from "./OrDivider";
import { PlanAccount } from "./PlanAccount";
import { UnlockPlanButton } from "./UnlockPlanButton";
import { useUnlockPlanAction } from "./useUnlockPlanAction";

export function UnlockPlanOrAlternative({
  recommendedPlan,
  cheaperPlan,
  account,
  currentPlan,
  ...props
}: {
  currentPlan: Plan | undefined;
  recommendedPlan: Plan;
  cheaperPlan: Plan;
  account: PlanAccount;
} & StackProps): JSX.Element {
  const { unlockPlanAction, isWorking } = useUnlockPlanAction({
    subscriptions: account.subscriptions,
  });
  const [recommendedPlanClick, setRecommendedPlanClick] = useState(false);
  const [cheaperPlanClick, setCheaperPlanClick] = useState(false);

  const canUnlock = canUnlockPlanFeature(account);

  const showCheaperPlan =
    !currentPlan &&
    cheaperPlan._id !== recommendedPlan._id &&
    cheaperPlan.price < recommendedPlan.price;

  useEffect(() => {
    // reset state if we had an error
    if (!isWorking) {
      setCheaperPlanClick(false);
      setRecommendedPlanClick(false);
    }
  }, [isWorking]);

  return (
    <VStack w="full" align="start" gap={6} divider={<OrDivider />} {...props}>
      <UnlockPlanButton
        planName={recommendedPlan.name}
        onClick={() => {
          setRecommendedPlanClick(true);
          unlockPlanAction(recommendedPlan);
        }}
        isLoading={recommendedPlanClick}
        isDisabled={isWorking}
        account={account}
      />

      {showCheaperPlan && (
        <HStack justify="space-between" alignItems="center" w="full">
          <Text>
            You can still unlock the{" "}
            <strong>
              {cheaperPlan.name} plan for ${cheaperPlan.price / 100}
            </strong>
            .
          </Text>
          <Tooltip
            shouldWrapChildren
            label={!canUnlock ? BILLING_OWNER_REQUIRED_MESSAGE : undefined}
          >
            <Button
              onClick={() => {
                setCheaperPlanClick(true);
                unlockPlanAction(cheaperPlan);
              }}
              isLoading={cheaperPlanClick}
              disabled={!canUnlock || isWorking}
              data-event={`purchase-start-${cheaperPlan.name}`}
            >{`Buy ${cheaperPlan.name}`}</Button>
          </Tooltip>
        </HStack>
      )}
    </VStack>
  );
}
